import React, { memo, useState } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Image, Progress, Typography, Button, message, Select, InputNumber, Row, Col, Space, Tooltip, Divider } from 'antd';
import { FileImageOutlined, PictureOutlined, FontSizeOutlined, PlayCircleFilled, LoadingOutlined, ExpandAltOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useGeneration } from '../../../context/GenerationContext';

import { useParams } from 'react-router-dom';
import api from '../../../api/client';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';

const { Meta } = Card;
const { Text } = Typography;

const MidjourneyNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { getNodes, getEdges, setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Parameters with defaults
    const ar = data.ar || "16:9";
    const chaos = data.chaos !== undefined ? data.chaos : 0;
    const stylize = data.stylize !== undefined ? data.stylize : 100;
    const weird = data.weird !== undefined ? data.weird : 0;
    const quality = data.quality || 1;
    const speed = data.speed || "fast";

    const updateData = (key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        [key]: value
                    }
                };
            }
            return node;
        }));

        // Persist to DB
        if (data.onSave) {
            data.onSave(id, { [key]: value });
        }
    };

    const { trackGeneration } = useGeneration();

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            // Find connected source node (looking for edge connected to 'left-top')
            const appEdges = getEdges();
            const sourceEdge = appEdges.find(
                (e) => e.target === id && e.targetHandle === 'left-top'
            );

            if (!sourceEdge) {
                message.warning('Please connect a text node to the top-left handle.');
                setGenerating(false);
                return;
            }

            const appNodes = getNodes();
            const sourceNode = appNodes.find((n) => n.id === sourceEdge.source);

            if (!sourceNode || !sourceNode.data || !sourceNode.data.text) {
                message.warning('Connected node has no text content.');
                setGenerating(false);
                return;
            }

            const basePrompt = sourceNode.data.text;

            // Construct the prompt string with flags
            let prompt = basePrompt;

            // Note: The backend could handle this, but explicit flags are safer for Midjourney
            prompt += ` --ar ${ar}`;
            if (chaos > 0) prompt += ` --c ${chaos}`;
            if (stylize !== 100) prompt += ` --s ${stylize}`;
            if (weird > 0) prompt += ` --w ${weird}`;
            if (quality !== 1) prompt += ` --q ${quality}`;

            // Special flags
            if (speed === 'turbo') prompt += ` --turbo`;
            if (speed === 'relax') prompt += ` --relax`;

            console.log('Generating Midjourney (Async):', prompt);

            // Send request to backend
            // Now returns immediately with { job_id, status }
            const response = await api.post('/legnext/diffusion', {
                text: prompt,
                canvas_id: canvasId,
                node_id: id,
                // We also send structured data just in case we enhance backend logging/logic later
                ar, chaos, stylize, weird, quality
            });

            const { job_id } = response.data;

            // Track in Queue
            trackGeneration(job_id, 'midjourney', {
                prompt: 'Midjourney Generation',
                type: 'Image',
                provider: 'legnext' // Critical for context to know which endpoint to poll
            }, {
                onComplete: (result) => {
                    console.error('Async Generation Complete Result:', JSON.stringify(result, null, 2)); // Use error to ensure visibility

                    if (result && result.images && result.images.length > 0) {
                        const images = result.images;
                        // Backend already persisted these
                        const indexUrl = images[0].url;

                        console.log('Processing images:', images);

                        // Update node
                        setNodes((nds) => nds.map((node) => {
                            if (node.id === id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        src: indexUrl,
                                        generatedImages: images
                                    }
                                };
                            }
                            return node;
                        }));

                        // Save
                        if (data.onSave) {
                            data.onSave(id, { src: indexUrl, generatedImages: images });
                        }

                        message.success('Generation complete!');
                    } else {
                        console.error('No images found in result:', result);
                        message.error('No images returned.');
                    }
                    setGenerating(false);
                },
                onFail: (err) => {
                    console.error("Generation failed via queue:", err);
                    setGenerating(false);
                }
            });

            // Keep "generating" true so the node shows loading state until callback
            // But we can also rely on the Queue UI
            // The original design kept the button spinning until completion
            // setGenerating(false); // Do NOT set false here, wait for completion

        } catch (error) {
            console.error('Generation error:', error);
            message.error('Generation failed: ' + (error.response?.data?.error || error.message));
            setGenerating(false);
        }
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={300}
                onResizeEnd={(e, params) => data.onResize && data.onResize(id, params)}
            />
            <NodeLabel
                title={data.nodeTitle || 'Midjourney'}
                iconType="fire"
                color="linear-gradient(135deg, #722ed1 0%, #1677ff 100%)"
                onTitleChange={onTitleChange}
            />

            {/* Inputs */}
            <Handle type="target" position={Position.Left} id="left-top" style={getHandleStyle(TYPE_COLORS.text, 'left-top', handlesVisible)} />
            <Handle type="source" position={Position.Right} style={getHandleStyle(TYPE_COLORS.image, 'right', handlesVisible)} />

            {/* Play Button */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 72, color: '#fff' }} /> : <PlayCircleFilled style={{ fontSize: 72, color: '#fff' }} />}
                onClick={handleGenerate}
                style={{
                    position: 'absolute',
                    right: -180,
                    bottom: 0,
                    width: 150,
                    height: 150,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '12px solid #161b22',
                    background: 'linear-gradient(135deg, #722ed1 0%, #1677ff 100%)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: generating ? '1px solid #fff' : '1px solid #30363d',
                    boxShadow: generating ? '0 0 20px rgba(255,255,255,0.3)' : '0 4px 12px rgba(0,0,0,0.5)',
                    display: 'flex', flexDirection: 'column',
                    borderRadius: 8,
                    background: '#000',
                    overflow: 'hidden'
                }}
            >
                {/* Image Area */}
                <div style={{ position: 'relative', flex: 1, overflow: 'hidden', background: '#111' }}>
                    {generating && <div className="shimmer-overlay" />}
                    {data.src ? (
                        <div
                            style={{ position: 'relative', width: '100%', height: '100%' }}
                            onMouseEnter={(e) => e.currentTarget.querySelector('.preview-overlay').style.opacity = 1}
                            onMouseLeave={(e) => e.currentTarget.querySelector('.preview-overlay').style.opacity = 0}
                        >
                            <Image
                                src={data.src}
                                alt="midjourney output"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                preview={{ visible: previewVisible, onVisibleChange: (vis) => setPreviewVisible(vis) }}
                            />
                            <div
                                className="preview-overlay nodrag"
                                onClick={(e) => { e.stopPropagation(); setPreviewVisible(true); }}
                                style={{
                                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                    background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    opacity: 0, transition: 'opacity 0.2s', cursor: 'pointer'
                                }}
                            >
                                <ExpandAltOutlined style={{ fontSize: 24, color: '#fff' }} />
                            </div>
                        </div>
                    ) : (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333' }}>
                            <div style={{ textAlign: 'center' }}>
                                <PictureOutlined style={{ fontSize: 48, marginBottom: 8 }} />
                                <div>Midjourney</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Controls Area */}
                <div style={{
                    padding: '12px',
                    background: '#161b22',
                    borderTop: '1px solid #30363d',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'center'
                }} className="nodrag">

                    <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                        <div style={{ flex: 1 }}>
                            <Text style={{ color: '#8b949e', fontSize: 10, display: 'block' }}>Aspect Ratio</Text>
                            <Select
                                value={ar}
                                onChange={(v) => updateData('ar', v)}
                                style={{ width: '100%' }}
                                size="small"
                                options={[
                                    { value: '16:9', label: '16:9' },
                                    { value: '3:2', label: '3:2' },
                                    { value: '1:1', label: '1:1' },
                                    { value: '2:3', label: '2:3' },
                                    { value: '9:16', label: '9:16' },
                                ]}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 4 }}>
                        <div style={{ flex: 1 }}>
                            <Text style={{ color: '#8b949e', fontSize: 10, display: 'block' }}>Stylize (0-1000)</Text>
                            <InputNumber
                                min={0} max={1000}
                                value={stylize}
                                onChange={(val) => updateData('stylize', val)}
                                size="small"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Text style={{ color: '#8b949e', fontSize: 10, display: 'block' }}>Chaos (0-100)</Text>
                            <InputNumber
                                min={0} max={100}
                                value={chaos}
                                onChange={(val) => updateData('chaos', val)}
                                size="small"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Text style={{ color: '#8b949e', fontSize: 10, display: 'block' }}>Weird (0-3000)</Text>
                            <InputNumber
                                min={0} max={3000}
                                value={weird}
                                onChange={(val) => updateData('weird', val)}
                                size="small"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                </div>
            </Card>

            {/* Thumbnail Gallery */}
            {data.generatedImages && data.generatedImages.length > 1 && (
                <div className="nodrag" style={{
                    position: 'absolute',
                    top: 220,
                    right: -136,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: 4,
                }}>
                    {data.generatedImages.map((img, idx) => (
                        <div
                            key={idx}
                            onClick={(e) => {
                                e.stopPropagation();
                                updateData('src', img.url);
                            }}
                            style={{
                                width: 120,
                                height: 120,
                                borderRadius: 6,
                                border: data.src === img.url ? '2px solid #fff' : '2px solid transparent',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                background: '#161b22',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                            }}
                        >
                            <img src={img.url} alt={`gen-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(MidjourneyNode);
